import * as _ from 'lodash';
import { UntypedFormControl, UntypedFormGroup, AbstractControl, Validators } from '@angular/forms';
import { CustomValidators } from 'shared/validation/custom-validators';
import { minLengthSpec, maxLengthSpec } from 'shared/validation/lengthSpec-validation';
import { fractionsSymbols } from './fractions-symbols';
import { specialSymbols } from './special-symbols';

import LanguageCodeMapper from './language-code-mapper';


export const getItemProperty = (item: any, path: string, detailsData: any): any => {
  if (!item) { return ''; }
  const pathName = path ? path : item.item_name;
  let property = _.get(detailsData, pathName, '');
  if (item.lookup && item.lookup.length && property) {
    const lookup = item.lookup.find((_item: any) => _item.option === property);
    property = lookup ? lookup.title : '';
  } else if (item.api_lookup) {
    property = _.get(detailsData, item.api_lookup.item_name, '');
  }
  return property;
};


export const getFieldValue = (item: any, field: any): string => {
  let value = '';
  if (_.isObject(item)) {
      value = _.get(item, field.item_name, '');
  } else {
    value = item;
  }

  if (field.lookup) {
    const lookupValue: any = _.find(field.lookup, {option: value});
    value = lookupValue ? lookupValue.title : value;
  } else if (field.api_lookup) {
    value = _.get(item, field.api_lookup.item_name, '');
  }

  if (field.item_name === 'resolution' && value !== undefined && value !== null) {
    value = `${value} dpi`;
  }

  return value;
};

export const getFieldTitle = (name: string, data: any): string => {
  const item: any = _.find(data, {item_name: name});
  return item ? item.title : '';
};

export const getFieldByName = (name: string, data: any): any => {
  return _.find(data, {item_name: name});
};

export const getFieldByMap = (map: string, data: any): any => {
  return _.find(data, {field_map: map});
};

export const getFieldTitleByFieldMap = (fieldMap: string, data: any): string => {
  const item: any = getFieldByMap(fieldMap, data);
  return item ? item.title : '';
};

export const getFieldNameByFieldMap = (fieldMap: string, data: any): string => {
  const item: any = getFieldByMap(fieldMap, data);
  return item ? item.item_name : '';
};

export const getLookup = (name: string, data: any): any => {
  const item: any = _.find(data, {item_name: name});
  return item ? item.lookup : null;
};

export const getLookupTitle = (option: string, lookup: Array<any>): string => {
  const item = _.find(lookup, (_item: any) => _item.option === option);
  return item ? item.title : option;
};

export const getOptions = (name: string, data: Array<any>): any[] => {
  // @ts-ignore
  const segment: any = _.find(this.segmentData, {item_name: name});
  let options = [];
  if (segment && segment.lookup) {
    options = segment.lookup.map((item: any) => {
      return {
        id: item.option,
        name: item.title,
      };
    });
  }
  return options;
};

export const getMaxLength = (item: any): number => {
  if (item.validation && item.validation.PIES_6_5) {
    return item.validation.PIES_6_5.maxLength;
  }
  return 0;
};

export const getFieldCellSize = (fieldName: string, fieldsInfo: any): string => {
  // for fieldInfo - get field cell size
  if (fieldsInfo.fullSizeFields && _.includes(fieldsInfo.fullSizeFields, fieldName)) {
    return 'full';
  } else if (fieldsInfo.threeQuartersSizeFields && _.includes(fieldsInfo.threeQuartersSizeFields, fieldName)) {
    return 'three-quarters';
  } else if (fieldsInfo.quarteredSizeFields && _.includes(fieldsInfo.quarteredSizeFields, fieldName)) {
    return 'quartered';
  } else if (fieldsInfo.oneSixthSizeFields && _.includes(fieldsInfo.oneSixthSizeFields, fieldName)) {
    return 'one-sixth';
  } else if (fieldsInfo.oneThirdSizeFields && _.includes(fieldsInfo.oneThirdSizeFields, fieldName)) {
    return 'one-third';
  } else {
    return 'half';
  }
};

export const switchField = (fieldName: string, fieldsInfo: any): string  => {
  // for fieldInfo - case field type
  if (fieldsInfo.readonlyFields && _.includes(fieldsInfo.readonlyFields, fieldName)) {
    return 'readonly';
  } else if (fieldsInfo.selectFields && _.includes(fieldsInfo.selectFields, fieldName)) {
    return 'select';
  } else if (fieldsInfo.textFields && _.includes(fieldsInfo.textFields, fieldName)) {
    return 'text';
  } else if (fieldsInfo.typeaheadFields && _.includes(fieldsInfo.typeaheadFields, fieldName)) {
    return 'typeahead';
  } else if (fieldsInfo.loopingFields && _.includes(fieldsInfo.loopingFields, fieldName)) {
    return 'loop';
  } else if (fieldsInfo.dateFields && _.includes(fieldsInfo.dateFields, fieldName)) {
    return 'date';
  } else if (fieldsInfo.customFields && _.includes(fieldsInfo.customFields, fieldName)) {
    return 'custom';
  } else {
    return 'input';
  }
};

export const applyMixins = (derivedCtor: any, baseCtors: any[]): void => {
  baseCtors.forEach(baseCtor => {
    Object.getOwnPropertyNames(baseCtor.prototype).forEach(name => {
      derivedCtor.prototype[name] = baseCtor.prototype[name];
    });
  });
};


// forms
export const initValidators = (validation: any, bind?: any): any[] => {
  if (!validation) { return []; }
  const validators = [];
  if (validation.required) { validators.push(Validators.required); }
  if (validation.length) {
    // use minLengthSpec & maxLengthSpec which counts special symbols correct
    validators.push(minLengthSpec(validation.length));
    validators.push(maxLengthSpec(validation.length));
  }
  if (validation.minLength) {
    // use minLengthSpec which counts special symbols correct
    validators.push(minLengthSpec(validation.minLength));
  }
  if (validation.maxLength) {
    // use maxLengthSpec which counts special symbols correct
    validators.push(maxLengthSpec(validation.maxLength));
  }
  if (validation.min) { validators.push(CustomValidators.min(validation.min)); }
  if (validation.max) { validators.push(CustomValidators.max(validation.max)); }
  if (validation.number) { validators.push(CustomValidators.number); }

  if (validation.custom) {
    const customValidator = validation.custom.bind(bind);
    validators.push(customValidator());
  }

  if (validation.conditionals) {
    const conditionalsValidator = validation.conditionals.bind(bind);
    validators.push(conditionalsValidator());
  }

  if (validation.email) { validators.push(Validators.email); }
  if (validation.pattern) { validators.push(Validators.pattern(validation.pattern)); }
  if (validation.validator) { validators.push(validation.validator); }

  return validators;
};


export const getItemValidators = (itemValidation: any, bind?: any, validationType?: string): any => {
  if (!itemValidation) { return []; }
  if (validationType) {
    return initValidators(_.get(itemValidation, validationType), bind);
  } else {
    const validators: any[] = [];
    _.each(itemValidation, (validations: any) => {
      Array.prototype.push.apply(validators, initValidators(validations, bind));
    });
    return validators;
  }
};

/*
  * pass field_map with formGroup or formControl only
*/
export const hasError = (field: any, form: UntypedFormGroup | AbstractControl): boolean => {
  if (!form) { return false; }
  if (!field) { return (<AbstractControl>form).invalid; }
  if (!(<UntypedFormGroup>form).controls[field]) { return false; }
  return (<UntypedFormGroup>form).controls[field].invalid;
};


export const populateModel = (Model: any, segmentData: any, itemDetail: any, bind?: any, validationType?: string): any => {
  const model = new Model();
  _.each(segmentData, (item: any) => {
    if (!item.field_map) { return; }
    const value = _.get(itemDetail, item.item_name, '');
    const validators = getItemValidators(item.validation, bind, validationType);
    const asyncValidators = getItemValidators(item.asyncValidation, bind, validationType);
    _.set(model, item.field_map, new UntypedFormControl(value, validators, asyncValidators));
  });
  return model;
};

export const getAvailableLanguages = (): Array<any> => {
  return [
    {
      title: LanguageCodeMapper.getName('en'),
      active: true,
      code: 'EN'
    },
    {
      title: LanguageCodeMapper.getName('es'),
      active: false,
      code: 'ES'
    },
    {
      title: LanguageCodeMapper.getName('fr'),
      active: false,
      code: 'FR'
    },
  ];
};

export const hasServerError = (itemDetailValidation: any, path: string, language?: string,
                               index?: number, notStrict?: boolean): boolean => {
  if (!itemDetailValidation) { return false; }
  if (language) { path += `.${language}`; }
  if (index !== undefined) {
    path += `.${index}`;
  }
  if (_.has(itemDetailValidation, 'combinedErrors')) {
    // if true, it searches partial match in the string
    // e.g. return true for 'attribute.0.value' in the 'attribute.0.value.0'
    if (notStrict) {
      return _.some(itemDetailValidation.combinedErrors, (error: string) => {
        return error.indexOf(path) > -1;
      });
    } else {
      return itemDetailValidation.combinedErrors.indexOf(path) > -1;
    }
  } else {
    return false;
  }
};

export const controlValidation = (item: any, formControl: AbstractControl): any => {
  if (!item.validation || !formControl) { return []; }
  return _.map(item.validation, (value: any, key: string) => {
    /*
    * TODO: We need to readdress how handle validating different types.
    * TODO: We need to add ability to handle multiple validations.
    * Only for one validation now.
    */
    return {
      name: key,
      // valid: !_.some(_.keys(value), (key: string) => _.has(control.errors, key.toLowerCase())),
      valid: formControl.valid,
    };
  });
};

export const fieldValidation = (item: any, formGroup: UntypedFormGroup): any => {
  if (!item.validation || !formGroup) { return []; }
  const control = formGroup.controls[item.field_map];
  return controlValidation(item, control);
};

export const fieldErrorDetail = (item: any, formGroup: UntypedFormGroup) => {
  if (!item || !item.title || !formGroup) { return ''; }
  const control = formGroup.controls[item.field_map];
  if (control.valid) { return ''; }
  if (control.errors) {
    const error = _.values(control.errors);
    if (_.isString(error[0])) { return error[0]; }
  }
  /*
  * TODO: We need to add ability to handle multiple validations.
  * Only for one validation now.
  */
  const keys  = _.keys(item.validation);
  if (!keys.length) { return ''; }
  return _.get(item, `validation.${keys[0]}.message`, '');
};

// str: 'example1@ex.com,example2@ex.com'
export const getEmailListFromStr = (str: string): Array<string> => {
  // remove whitespaces
  let trimValue = _.trim(str).replace(/ /, '');

  // if there is , on the end - remove it
  while (trimValue[trimValue.length - 1] === ',') {
    trimValue = trimValue.substring(0, trimValue.length - 1);
  }

  return trimValue.split(',');
};

// returns a random integer between the specified values
// The maximum is exclusive and the minimum is inclusive
export const getRandomInt = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min)) + min;
};
